import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';

const LOGO = "/assets/logo-puro.png";
const LOGO_FILTER = { filter: 'brightness(0) saturate(100%) invert(45%) sepia(28%) saturate(520%) hue-rotate(357deg) brightness(0.92)' };

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3500);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        setError("Connexion au serveur impossible. Réessayez dans un instant.");
      } else if (status === 400 && typeof detail === 'string') {
        // Backend gives precise message : "Lien invalide ou déjà utilisé." / "Lien expiré..."
        setError(detail);
      } else if (status === 500) {
        setError("Impossible de mettre à jour le mot de passe. Merci de demander un nouveau lien de réinitialisation.");
      } else {
        setError(typeof detail === 'string' ? detail : "Nous n'avons pas pu mettre à jour votre mot de passe. Demandez un nouveau lien.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F5EFE5] flex items-center justify-center px-4">
        <Card className="border-[#E9DDCC] rounded-2xl max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h1 className="font-heading text-xl text-[#493329] mb-3">Lien invalide</h1>
            <p className="text-sm text-[#7A6455] mb-6">Aucun token de réinitialisation détecté. Demandez un nouveau lien.</p>
            <Link to="/forgot-password">
              <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11 px-6">
                Demander un nouveau lien
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE5] flex flex-col">
      <div className="p-4 sm:p-6">
        <button onClick={() => navigate('/login')} className="inline-flex items-center text-[#7A6455] hover:text-[#C99A55] transition-colors text-sm">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour à la connexion
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-5" style={LOGO_FILTER} />
            <h1 className="font-heading font-medium text-2xl sm:text-3xl text-[#493329] tracking-tight">
              {done ? 'Mot de passe mis à jour' : 'Nouveau mot de passe'}
            </h1>
            <div className="w-8 h-0.5 bg-[#C99A55] mx-auto mt-3" />
          </div>

          {done ? (
            <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C99A55]/15 flex items-center justify-center">
                  <ShieldCheck className="w-7 h-7 text-[#C99A55]" strokeWidth={1.5} />
                </div>
                <p className="text-[#493329] text-base mb-3 font-medium">C'est fait !</p>
                <p className="text-sm text-[#7A6455] leading-relaxed mb-6">
                  Votre mot de passe a été mis à jour avec succès. Vous allez être redirigée vers la page de connexion.
                </p>
                <Link to="/login">
                  <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11 px-8 transition-all duration-300" data-testid="reset-back-login">
                    Se connecter
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardContent className="p-6 sm:p-8">
                <p className="text-sm text-[#7A6455] mb-6 leading-relaxed">
                  Choisissez votre nouveau mot de passe. Il doit contenir au moins 6 caractères.
                </p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <Alert variant="destructive" className="bg-[#C76542]/10 border-[#C76542]/20 text-[#C76542]">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="pw" className="text-[#493329] text-sm">Nouveau mot de passe</Label>
                    <Input
                      id="pw" type="password" required minLength={6}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="h-12 rounded-xl border-[#E9DDCC] bg-white"
                      placeholder="6 caractères minimum"
                      data-testid="reset-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pw2" className="text-[#493329] text-sm">Confirmation</Label>
                    <Input
                      id="pw2" type="password" required minLength={6}
                      value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      className="h-12 rounded-xl border-[#E9DDCC] bg-white"
                      placeholder="Retapez le mot de passe"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#C99A55] hover:bg-[#493329] text-white h-12 rounded-xl btn-premium transition-all duration-400 text-base"
                    disabled={loading}
                    data-testid="reset-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mettre à jour mon mot de passe'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
    </div>
  );
}
